import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const PHASE_LABEL = {
    never: "Mai eseguito",
    starting: "Avvio…",
    index: "Indicizzazione",
    details: "Dettagli giocatori",
    done: "Completato",
    cancelled: "Annullato",
    error: "Errore",
};

export default function AdminPesdbSync() {
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [batchSize, setBatchSize] = useState(300);
    const [vgMin, setVgMin] = useState(74);
    const [importResult, setImportResult] = useState(null);
    const [allImportResult, setAllImportResult] = useState(null);
    const [tmStatus, setTmStatus] = useState(null);
    const [tmLinksInfo, setTmLinksInfo] = useState(null);
    const [hiddenDups, setHiddenDups] = useState(null);
    const [posStatus, setPosStatus] = useState(null);
    const [pipeline, setPipeline] = useState(null);
    const [pipelineReset, setPipelineReset] = useState(false);
    const [importPreview, setImportPreview] = useState(null); // {kind:'roster'|'tm', diff, items}
    const pollRef = useRef(null);

    // Lo scraping PESDB funziona solo in Preview (in produzione l'IP è bloccato).
    const isProd = typeof window !== "undefined" && window.location.hostname.endsWith("emergent.host");

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/pesdb/status");
            setStatus(data);
        } catch (e) {
            // ignore
        }
        try {
            const { data } = await api.get("/legacy/tm-status");
            setTmStatus(data);
        } catch (e) {
            // ignore
        }
        try {
            const { data } = await api.get("/pesdb/position-ratings/status");
            setPosStatus(data);
        } catch (e) {
            // ignore
        }
        try {
            const { data } = await api.get("/pesdb/pipeline/status");
            setPipeline(data);
        } catch (e) {
            // ignore
        }
    }, []);

    /* eslint-disable */
    useEffect(() => {
        fetchStatus();
        pollRef.current = setInterval(fetchStatus, 2500);
        return () => clearInterval(pollRef.current);
    }, [fetchStatus]);
    /* eslint-enable */

    const resetRefreshFlag = async () => {
        if (!window.confirm("Preparare un nuovo giro completo?\n\nAzzera il flag 'già aggiornato' su TUTTI i giocatori, così 'Aggiorna VG + stili' ri-scaricherà da capo l'intera rosa (utile dopo un NUOVO aggiornamento del gioco).\n\nProcedere?")) return;
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post(`/pesdb/reset-refresh-flag`);
            alert(`Fatto: azzerati ${data.reset} giocatori. Ora lancia 'Aggiorna VG + stili' a lotti fino a 0, poi il Ponte.`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const resetPositionRatings = async () => {
        if (!window.confirm("Azzerare la cache della MAPPA RUOLI su TUTTI i giocatori?\n\nDopo, lancia il backfill per ri-scaricarle (in Preview), poi il Ponte per portarle in produzione. Da fare dopo un grande aggiornamento del gioco.\n\nProcedere?")) return;
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post(`/pesdb/reset-position-ratings`);
            alert(`Fatto: azzerate ${data.reset} mappe ruoli. Ora lancia il backfill mappe ruoli.`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const startPosBackfill = async () => {
        setErr("");
        setBusy(true);
        try {
            await api.post(`/pesdb/position-ratings/backfill`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const startPipeline = async () => {
        const msg = pipelineReset
            ? "Avviare l'AGGIORNAMENTO COMPLETO (dopo aggiornamento del gioco)?\n\nAzzera tutto e ri-scarica da capo: elenco, VG+stili, abilità svincolati, mappe ruoli e giro Transfermarkt. Gira in sequenza, lentamente, anche per ore. Puoi chiudere la pagina.\n\nAl termine ricordati il Ponte verso produzione."
            : "Avviare l'aggiornamento completo (solo i mancanti)?\n\nCompleta ciò che manca: elenco, VG+stili, abilità svincolati, mappe ruoli e giro Transfermarkt. Gira in sequenza, lentamente. Puoi chiudere la pagina.";
        if (!window.confirm(msg)) return;
        setErr("");
        setBusy(true);
        try {
            await api.post(`/pesdb/pipeline/start?reset=${pipelineReset}`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const loadHiddenDups = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/hidden-duplicates`);
            setHiddenDups(data.items || []);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const unhideDup = async (pesId) => {
        try {
            await api.post(`/players/${pesId}/unhide-duplicate`);
            await loadHiddenDups();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };


    const cancelPipeline = async () => {
        if (!window.confirm("Interrompere l'aggiornamento completo? (i dati già scaricati restano salvati)")) return;
        try {
            await api.post(`/pesdb/pipeline/cancel`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };


    const startSync = async (mode, extra = "") => {
        setErr("");
        setBusy(true);
        try {
            await api.post(`/pesdb/sync?mode=${mode}${extra}`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const startFreeAgentsSkills = () => {
        const n = Math.max(1, Math.min(2000, Number(batchSize) || 300));
        const vg = Math.max(40, Math.min(99, Number(vgMin) || 74));
        startSync("free_agents_skills", `&limit=${n}&vg_min=${vg}`);
    };

    // PONTE Preview → Produzione: esporta le abilità scrapate in Preview, poi importale in Produzione.
    const exportSkills = async () => {
        setErr("");
        setBusy(true);
        try {
            const vg = Math.max(40, Math.min(99, Number(vgMin) || 74));
            const { data } = await api.get(`/pesdb/export-skills?scope=free_agents&vg_min=${vg}`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `abilita-svincolati-vg${vg}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const importSkills = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setErr("");
        setImportResult(null);
        setBusy(true);
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const items = Array.isArray(parsed) ? parsed : parsed.items || [];
            const { data } = await api.post(`/pesdb/import-skills`, { items });
            setImportResult(data);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "File non valido");
        } finally {
            setBusy(false);
        }
    };

    // PONTE UNICO: esporta in un solo file TUTTI i dati (PESDB + Transfermarkt).
    const exportAll = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.get(`/pesdb/export-all`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `dati-completi.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const importAllFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setErr("");
        setAllImportResult(null);
        setBusy(true);
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const items = Array.isArray(parsed) ? parsed : parsed.items || [];
            // ANTEPRIMA: mostra ogni singolo cambiamento prima di applicare.
            const { data } = await api.post(`/pesdb/import-all/preview`, { items });
            setImportPreview({ diff: data, items });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "File non valido");
        } finally {
            setBusy(false);
        }
    };

    // ---- Transfermarkt: giro completo (valore + immagine + età) ----
    const startTmFullRefresh = async () => {
        if (!window.confirm("Avviare il giro TM COMPLETO?\n\nAggiorna valore di mercato + immagine + età per tutti i giocatori in rosa con link Transfermarkt (in background). Scrape diretto da transfermarkt.it.\n\nProcedere?")) return;
        setErr("");
        setBusy(true);
        try {
            await api.post(`/legacy/tm-full-refresh`);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const scanTmLinks = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.get(`/pesdb/tm-links/malformed`);
            setTmLinksInfo(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const fixTmLinks = async () => {
        if (!window.confirm("Correggere in blocco i tm_link malformati (rimuove spazi/'/%20' iniziali)?\n\nSolo dati, nessuno scraping. Idempotente.")) return;
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post(`/pesdb/tm-links/malformed/fix`);
            alert(`Fatto: ${data.fixed} link corretti.${data.unrecoverable_count ? ` ${data.unrecoverable_count} irrecuperabili (senza http) — vanno sistemati a mano.` : ""}`);
            await scanTmLinks();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };


    // Applica l'import dei SOLI cambiamenti accettati nell'anteprima.
    const confirmImport = async (accepted) => {
        if (!importPreview) return;
        const { items } = importPreview;
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post(`/pesdb/import-all`, { items, accepted });
            setAllImportResult(data);
            setImportPreview(null);
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const cancelSync = async () => {        try {
            await api.post("/pesdb/sync/cancel");
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const indexPct =
        status?.total_pages && status?.pages_done
            ? Math.min(100, (status.pages_done / status.total_pages) * 100)
            : 0;
    const detailsPct =
        status?.details_total && status?.details_done
            ? Math.min(100, (status.details_done / status.details_total) * 100)
            : 0;

    return (
        <div data-testid="admin-pesdb-sync">
            {importPreview && (
                <ImportPreviewModal
                    preview={importPreview}
                    busy={busy}
                    onConfirm={confirmImport}
                    onCancel={() => setImportPreview(null)}
                />
            )}
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="op-heading text-3xl">PES DB Sync</h2>
                    <p className="op-label mt-2">Scaricamento e aggiornamento dati giocatori</p>
                </div>
                <div className="op-mono text-t8f95a3 text-xs">
                    Fonte: pesdb.net/efootball
                </div>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-4">
                    {err}
                </div>
            )}

            {/* One-button overnight orchestrator */}
            <div className="op-card p-6 mb-6 border-l-2 border-l-t2bb6ff" data-testid="pipeline-panel">
                <div className="op-label text-t2bb6ff mb-2">// AGGIORNA TUTTO (NOTTURNO)</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4 max-w-3xl">
                    Un solo tasto: esegue in sequenza <strong>tutti</strong> i giri (indicizzazione, VG+stili,
                    abilità svincolati, mappe ruoli, giro Transfermarkt) lentamente e da solo — anche per ore.
                    Puoi chiudere la pagina. Al termine, ricordati il <strong>Ponte</strong> verso produzione (resta manuale).
                </p>

                {pipeline?.running ? (
                    <>
                        <div className="op-mono text-[13px] text-t2bb6ff mb-3" data-testid="pipeline-current">
                            ⏳ In corso — {pipeline.phase_label || pipeline.phase}
                            {pipeline.reset && <span className="text-tffd24b ml-2">(giro completo)</span>}
                        </div>
                        <ol className="space-y-1 mb-4" data-testid="pipeline-steps">
                            {(pipeline.steps || []).map((s) => (
                                <li key={s.key} className="op-mono text-[12px] flex items-center gap-2">
                                    <span className={
                                        s.status === "done" ? "text-t7ce38b"
                                        : s.status === "running" ? "text-t2bb6ff"
                                        : s.status === "partial" ? "text-tffd24b"
                                        : s.status === "error" ? "text-tff4d4d"
                                        : "text-t8f95a3"
                                    }>
                                        {s.status === "done" ? "✓" : s.status === "running" ? "▶" : s.status === "partial" ? "◐" : s.status === "error" ? "✗" : s.status === "skipped" ? "–" : "○"}
                                    </span>
                                    <span className={s.status === "pending" || s.status === "skipped" ? "text-t8f95a3" : "text-tc9ccd3"}>{s.label}</span>
                                    {s.detail && <span className="text-t8f95a3">· {s.detail}</span>}
                                </li>
                            ))}
                        </ol>
                        <button onClick={cancelPipeline} className="op-btn op-btn-secondary px-4 py-2" data-testid="pipeline-cancel-btn">
                            ⛔ Interrompi
                        </button>
                    </>
                ) : (
                    <>
                        <label className="flex items-center gap-2 mb-4 cursor-pointer select-none" data-testid="pipeline-reset-label">
                            <input
                                type="checkbox"
                                checked={pipelineReset}
                                onChange={(e) => setPipelineReset(e.target.checked)}
                                className="accent-t2bb6ff w-4 h-4"
                                data-testid="pipeline-reset-checkbox"
                            />
                            <span className="text-sm text-tc9ccd3">È uscito un <strong>nuovo aggiornamento del gioco</strong> (azzera e ri-scarica tutto). Senza spunta: solo i mancanti.</span>
                        </label>
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={startPipeline}
                                disabled={busy || isProd || status?.running || posStatus?.running}
                                title={isProd ? "Solo in Preview (in produzione lo scraping è bloccato)" : ""}
                                className="op-btn op-btn-primary px-5 py-2.5 disabled:opacity-50"
                                data-testid="pipeline-start-btn"
                            >
                                🚀 Avvia adesso
                            </button>
                            {pipeline && pipeline.phase !== "never" && (
                                <span className="op-mono text-[12px] text-t8f95a3" data-testid="pipeline-last">
                                    {pipeline.cancelled ? "Ultimo giro: annullato"
                                        : pipeline.phase === "error" ? `Ultimo giro: errore (${pipeline.error || ""})`
                                        : pipeline.phase === "done" ? `Ultimo giro: completato ✔${pipeline.finished_at ? " · " + new Date(pipeline.finished_at).toLocaleString("it-IT") : ""}`
                                        : null}
                                </span>
                            )}
                        </div>
                        {isProd && (
                            <p className="op-mono text-[11px] text-tff7a1a mt-3">
                                In produzione lo scraping è bloccato: lancia questo giro in Preview e poi usa i Ponti.
                            </p>
                        )}
                    </>
                )}
            </div>

            {/* status panel */}
            <div className="grid grid-cols-2 lg:grid-cols-4 border border-t22252a divide-x divide-y lg:divide-y-0 divide-t22252a mb-6">
                <Stat label="Stato" value={PHASE_LABEL[status?.phase] || "—"} accent={status?.running ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-8f95a3))"} />
                <Stat label="Giocatori in DB" value={(status?.players_total ?? 0).toLocaleString("it-IT")} />
                <Stat label="Con dettagli completi" value={(status?.players_with_details ?? 0).toLocaleString("it-IT")} />
                <Stat label="Ultimo update" value={status?.updated_at ? new Date(status.updated_at).toLocaleTimeString("it-IT") : "—"} small />
            </div>

            {/* progress bars (visible during sync) */}
            {status?.running && (
                <div className="space-y-4 mb-6">
                    {status.phase === "index" && (
                        <ProgressBar
                            label="Indicizzazione pagine"
                            value={status.pages_done || 0}
                            total={status.total_pages || 0}
                            pct={indexPct}
                            unit="pagine"
                        />
                    )}
                    {status.phase === "details" && (
                        <>
                            <ProgressBar
                                label="Dettagli giocatori"
                                value={status.details_done || 0}
                                total={status.details_total || 0}
                                pct={detailsPct}
                                unit="giocatori"
                            />
                            {status.details_failed > 0 && (
                                <div className="op-mono text-[11px] text-tff7a1a mt-2" data-testid="details-failed">
                                    ⚠ {status.details_failed} falliti (probabile rate-limit di pesdb.net sull&apos;IP: riprova più tardi)
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Action buttons */}
            <div className="op-card p-6">
                <div className="op-label text-t2bb6ff mb-3">// AZIONI</div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <ActionCard
                        title="🔄 Prepara nuovo giro completo"
                        desc="Da usare SOLO dopo un NUOVO grande aggiornamento del gioco: azzera il flag 'già aggiornato' su tutti i giocatori, così 'Aggiorna VG + stili' ri-scarica da capo l'INTERA rosa (VG, statistiche, stili, ruolo, abilità). Poi lancia 'Aggiorna VG + stili' a lotti fino a 0, e infine il Ponte (Scarica → Importa in produzione)."
                        onClick={resetRefreshFlag}
                        disabled={busy || status?.running}
                        testId="reset-refresh-flag-btn"
                        accent="rgb(var(--tc-e8ff00))"
                    />
                    <ActionCard
                        title={`Aggiorna VG + statistiche + stili (rosa & svincolati)${status?.roster_styles_remaining ? ` · ${status.roster_styles_remaining} da fare` : " · completo ✓"}`}
                        desc="Dopo un aggiornamento di PESDB: ri-scarica overall Authentic (→ VG di rosa), statistiche e i nuovi stili Attacco/Difesa. RIPRENDIBILE: fa ~15 giocatori per volta (sotto la soglia di blocco di pesdb.net) e salta chi è già fatto. Clicca, aspetta che finisca (dovrebbe darti 0 falliti), breve pausa, poi ri-clicca — finché il contatore arriva a 0. Poi 'Scarica file dati' → importa in produzione."
                        onClick={() => startSync("refresh_roster", "&vg_min=80&limit=15")}
                        disabled={busy || status?.running}
                        testId="sync-refresh-roster-btn"
                        accent="rgb(var(--tc-2bb6ff))"
                    />
                    <ActionCard
                        title={`Sync giocatori in rosa senza dati PESDB${status?.roster_missing_details ? ` (${status.roster_missing_details})` : ""}`}
                        desc="Mirato: ri-scarica da PESDB SOLO i giocatori in rosa col record vuoto/incompleto (o senza record). Riempie quelli con id ancora valido; gli id rimossi da pesdb.net falliscono e vanno ri-agganciati per nome. Eseguire in PREVIEW."
                        onClick={() => startSync("roster_missing")}
                        disabled={busy || status?.running}
                        testId="sync-roster-missing-btn"
                        accent="rgb(var(--tc-ff7a1a))"
                    />
                    <ActionCard
                        title="Sync abilità rose (consigliato)"
                        desc="~5-10 minuti. Riscarica le ABILITÀ e gli stili di gioco SOLO dei giocatori presenti nelle rose (ignora chi è già stato sincronizzato). Risolve 'nessuno ha le abilità'."
                        onClick={() => startSync("roster_skills")}
                        disabled={busy || status?.running}
                        testId="sync-roster-skills-btn"
                        accent="rgb(var(--tc-7ce38b))"
                    />
                    <ActionCard
                        title="Sync veloce (indice)"
                        desc="~3 minuti. Scarica nome, posizione, club, nazionalità, overall di tutti i giocatori. Ricerca subito disponibile."
                        onClick={() => startSync("index")}
                        disabled={busy || status?.running}
                        testId="sync-index-btn"
                        accent="rgb(var(--tc-2bb6ff))"
                    />
                    <ActionCard
                        title="Sync dettagli completi"
                        desc="~30-60 minuti. Scarica tutte le statistiche dettagliate, foto in-game, stile di gioco, resistenza infortuni (solo giocatori non ancora sincronizzati)."
                        onClick={() => startSync("details")}
                        disabled={busy || status?.running}
                        testId="sync-details-btn"
                        accent="rgb(var(--tc-e8ff00))"
                    />
                    <ActionCard
                        title="Sync FULL (indice + dettagli)"
                        desc="Esegue prima l'indice poi i dettagli. Consigliato per il primo sync."
                        onClick={() => startSync("full")}
                        disabled={busy || status?.running}
                        testId="sync-full-btn"
                        accent="rgb(var(--tc-ff6b00))"
                    />
                </div>
                {status?.running && (
                    <button
                        onClick={cancelSync}
                        className="op-btn op-btn-ghost text-tff3b30 hover:text-tff3b30 mt-5"
                        data-testid="sync-cancel-btn"
                    >
                        Annulla sync in corso
                    </button>
                )}
            </div>

            {/* Novità di oggi: riepilogo dopo il Sync indice */}
            <NewsReport report={status?.last_index_report} />

            {/* PONTE UNICO: un file solo con tutti i dati (PESDB + Transfermarkt) */}
            <div className="op-card p-6 mt-6 border-l-2 border-l-te8ff00" data-testid="bridge-all-panel">
                <div className="op-label text-te8ff00 mb-3">// PONTE UNICO — SCARICA / CARICA TUTTO</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4 max-w-3xl">
                    Un solo file con <strong>tutti</strong> i dati sincronizzati: PESDB (ruolo, età, overall/VG, stili,
                    abilità, statistiche, mappa ruoli…) <strong>+</strong> Transfermarkt (valore, immagine, età).
                    <br /><strong>① in PREVIEW</strong> usa il tasto notturno “Aggiorna tutto” qui sopra finché è completo,
                    poi <strong>② Scarica file unico</strong>. <strong>③ in PRODUZIONE</strong> carica il file: prima di
                    toccare il database ti mostro <strong>ogni singolo cambiamento</strong> e scegli tu cosa applicare.
                </p>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <div className="border border-t22252a px-4 py-3 bg-t0e1014">
                        <div className="op-label text-[10px]">In rosa ancora senza dati PESDB</div>
                        <div className="op-mono font-bold text-2xl text-tff7a1a" data-testid="roster-missing-remaining">
                            {(status?.roster_missing_details ?? 0).toLocaleString("it-IT")}
                        </div>
                    </div>
                    <div className="border border-t22252a px-4 py-3 bg-t0e1014">
                        <div className="op-label text-[10px]">Id rimosso da PESDB (da ri-agganciare per nome)</div>
                        <div className="op-mono font-bold text-2xl text-tff3b30" data-testid="roster-removed-count">
                            {(status?.roster_removed_count ?? 0).toLocaleString("it-IT")}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={exportAll}
                        disabled={busy}
                        className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-5 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="export-all-btn"
                    >
                        ⬇ ② Scarica file unico (dati-completi.json)
                    </button>
                    <label
                        className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-5 py-2 cursor-pointer transition-colors"
                        data-testid="import-all-label"
                    >
                        ⬆ ③ Carica file unico (anteprima + scegli)
                        <input
                            type="file"
                            accept="application/json,.json"
                            onChange={importAllFile}
                            disabled={busy}
                            className="hidden"
                            data-testid="import-all-input"
                        />
                    </label>
                </div>
                {allImportResult && (
                    <div className="op-mono text-[12px] text-t7ce38b mt-3" data-testid="import-all-result">
                        ✓ {allImportResult.received} nel file · {allImportResult.players_updated} schede aggiornate · {allImportResult.players_created} create · {allImportResult.entries_updated} voci rosa aggiornate
                        {allImportResult.skipped > 0 && ` · ${allImportResult.skipped} saltati`}
                        {typeof allImportResult.vg_updated === "number" && ` · ${allImportResult.vg_updated} VG allineati`}
                    </div>
                )}
            </div>

            {/* TRANSFERMARKT — giro completo (valore + immagine + età) */}
            <div className="op-card p-6 mt-6 border-l-2 border-l-t2bb6ff" data-testid="tm-refresh-panel">
                <div className="op-label text-t2bb6ff mb-3">// TRANSFERMARKT — GIRO COMPLETO</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4">
                    Aggiorna in un&apos;unica passata <strong>valore di mercato + immagine + età</strong> di tutti i
                    giocatori in rosa con link Transfermarkt. Scrape diretto da transfermarkt.it, in background.
                    {tmStatus?.running && (
                        <span className="block mt-2 text-tffd24b">
                            ⏳ In corso: {tmStatus.players_done}/{tmStatus.players_total} · {tmStatus.updated} aggiornati
                            {tmStatus.failed ? ` · ${tmStatus.failed} falliti` : ""}
                        </span>
                    )}
                    {tmStatus && !tmStatus.running && tmStatus.phase === "done" && (
                        <span className="block mt-2 text-t7ce38b">
                            ✓ Ultimo giro: {tmStatus.updated} aggiornati su {tmStatus.players_total}
                            {tmStatus.failed ? ` · ${tmStatus.failed} falliti` : ""}
                        </span>
                    )}
                </p>
                <button
                    onClick={startTmFullRefresh}
                    disabled={busy || tmStatus?.running}
                    className="op-btn op-btn-primary px-4 py-2 disabled:opacity-50"
                    data-testid="tm-full-refresh-btn"
                >
                    {tmStatus?.running ? "Giro TM in corso…" : "Aggiorna valore + immagine + età (TM)"}
                </button>
            </div>

            {/* Pulizia tm_link malformati */}
            <div className="op-card p-6 mt-6 border-l-2 border-l-tff7a1a" data-testid="tm-links-cleanup-panel">
                <div className="op-label text-tff7a1a mb-3">// CORREGGI LINK TRANSFERMARKT STORTI</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4">
                    Alcuni <strong>tm_link</strong> nel database sono malformati (spazi o <code>/%20</code> iniziali):
                    facevano fallire quei giocatori durante il giro TM. Questo pulisce tutti i link recuperabili
                    in blocco (roster + schede). Solo dati, nessuno scraping, idempotente.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                    <button
                        onClick={scanTmLinks}
                        disabled={busy}
                        className="op-btn op-btn-secondary px-4 py-2 disabled:opacity-50"
                        data-testid="tm-links-scan-btn"
                    >
                        🔎 Controlla link storti
                    </button>
                    <button
                        onClick={fixTmLinks}
                        disabled={busy || !tmLinksInfo || !tmLinksInfo.fixable_count}
                        className="op-btn op-btn-primary px-4 py-2 disabled:opacity-50"
                        data-testid="tm-links-fix-btn"
                    >
                        🧹 Correggi {tmLinksInfo?.fixable_count || 0} link
                    </button>
                </div>
                {tmLinksInfo && (
                    <div className="op-mono text-[12px] mt-3" data-testid="tm-links-result">
                        <span className={tmLinksInfo.fixable_count ? "text-tffd24b" : "text-t7ce38b"}>
                            {tmLinksInfo.fixable_count} da correggere
                        </span>
                        {tmLinksInfo.unrecoverable_count > 0 && (
                            <span className="text-tff4d4d ml-2">· {tmLinksInfo.unrecoverable_count} irrecuperabili (senza http)</span>
                        )}
                        {tmLinksInfo.fixable_count > 0 && tmLinksInfo.fixable?.[0] && (
                            <div className="text-tc9ccd3 mt-2 break-all">
                                es: <span className="text-tff7a1a">{tmLinksInfo.fixable[0].from}</span> → <span className="text-t7ce38b">{tmLinksInfo.fixable[0].to}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>


            {/* Doppioni nascosti a mano — ripristino */}
            <div className="op-card p-6 mt-6 border-l-2 border-l-tff9800" data-testid="hidden-duplicates-panel">
                <div className="op-label text-tff9800 mb-3">// DOPPIONI NASCOSTI (A MANO)</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4 max-w-3xl">
                    Per nascondere un doppione (es. carta nazionale) apri la sua <strong>scheda giocatore</strong>
                    (dalla ricerca o dagli svincolati) e usa il pulsante <span className="text-tff7a1a">⊘ Nascondi doppione</span>.
                    Qui vedi tutte le carte nascoste a mano e puoi ripristinarle.
                </p>
                <button
                    onClick={loadHiddenDups}
                    disabled={busy}
                    className="op-btn op-btn-secondary px-4 py-2 disabled:opacity-50 mb-4"
                    data-testid="load-hidden-duplicates-btn"
                >
                    🔎 Mostra doppioni nascosti
                </button>
                {hiddenDups !== null && (
                    hiddenDups.length === 0 ? (
                        <div className="op-mono text-[12px] text-t7ce38b">Nessun doppione nascosto a mano.</div>
                    ) : (
                        <div className="divide-y divide-t1a1d24" data-testid="hidden-duplicates-list">
                            {hiddenDups.map((d) => (
                                <div key={d.pes_id} className="flex items-center justify-between py-2 gap-3">
                                    <div className="op-mono text-[12px] text-tc9ccd3">
                                        <span className="text-tffffff">{d.name}</span>
                                        {d.team_pes && <span className="text-t8f95a3"> · {d.team_pes}</span>}
                                        {typeof d.overall === "number" && <span className="text-t8f95a3"> · VG {d.overall}</span>}
                                        <span className="text-t8f95a3"> · id {d.pes_id}</span>
                                    </div>
                                    <button
                                        onClick={() => unhideDup(d.pes_id)}
                                        className="op-btn op-btn-secondary px-3 py-1 text-[11px] shrink-0"
                                        data-testid={`unhide-dup-${d.pes_id}`}
                                    >
                                        ♻ Ripristina
                                    </button>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>


            {/* Mappa ruoli (campo verde) — reset + backfill (Preview) + Ponte */}
            <div className="op-card p-6 mt-6 border-l-2 border-l-te8ff00" data-testid="position-ratings-panel">
                <div className="op-label text-te8ff00 mb-3">// MAPPA RUOLI (CAMPO)</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4">
                    La mappa ruoli sul campo verde (<strong>position_ratings</strong>) NON si aggiorna con "Aggiorna VG + stili":
                    va ri-scaricata a parte dopo un grande aggiornamento del gioco. Flusso: <strong>1)</strong> Azzera cache →
                    <strong> 2)</strong> Backfill (scarica da pesdb.net, solo Preview) → <strong>3)</strong> Ponte "dati rosa" per portarla in produzione.
                </p>
                <div className="op-mono text-[12px] text-tc9ccd3 mb-4" data-testid="position-ratings-status">
                    {posStatus ? (
                        <>
                            <span className={posStatus.remaining ? "text-tffd24b" : "text-t7ce38b"}>
                                {posStatus.remaining} mappe mancanti
                            </span>
                            {posStatus.running && (
                                <span className="text-t2bb6ff ml-2">
                                    · backfill in corso: {posStatus.players_done}/{posStatus.players_total} (agg. {posStatus.updated}, falliti {posStatus.failed})
                                </span>
                            )}
                            {!posStatus.running && posStatus.phase === "done" && (
                                <span className="text-t7ce38b ml-2">· ultimo giro: {posStatus.updated} aggiornate, {posStatus.failed} fallite</span>
                            )}
                            {!posStatus.running && posStatus.phase === "error" && (
                                <span className="text-tff4d4d ml-2">· errore: {posStatus.error}</span>
                            )}
                        </>
                    ) : "…"}
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                    <button
                        onClick={resetPositionRatings}
                        disabled={busy || posStatus?.running}
                        className="op-btn op-btn-secondary px-4 py-2 disabled:opacity-50"
                        data-testid="reset-position-ratings-btn"
                    >
                        🧹 Azzera cache mappe ruoli
                    </button>
                    <button
                        onClick={startPosBackfill}
                        disabled={busy || isProd || posStatus?.running || !posStatus?.remaining}
                        title={isProd ? "Disponibile solo in Preview (in produzione lo scraping è bloccato)" : ""}
                        className="op-btn op-btn-primary px-4 py-2 disabled:opacity-50"
                        data-testid="position-ratings-backfill-btn"
                    >
                        {posStatus?.running ? "⏳ Backfill in corso…" : `⬇️ Backfill mappe ruoli (${posStatus?.remaining || 0})`}
                    </button>
                </div>
                {isProd && (
                    <p className="op-mono text-[11px] text-tff7a1a mt-3">
                        In produzione lo scraping è bloccato: fai il backfill in Preview e poi usa il Ponte "dati rosa".
                    </p>
                )}
            </div>


            {/* Sync Abilità Svincolati — a lotti, solo in Preview */}
            <div className="op-card p-6 mt-6" data-testid="free-agents-skills-panel">
                <div className="op-label text-tff7a1a mb-3">// SYNC ABILITÀ SVINCOLATI (A LOTTI)</div>
                <p className="text-tc9ccd3 text-sm leading-relaxed mb-4">
                    Recupera da PESDB le <strong>abilità e gli stili di gioco</strong> dei giocatori
                    <strong> NON in rosa (svincolati)</strong> con <strong>VG ≥ {vgMin}</strong> che ne sono ancora sprovvisti.
                    Funziona <strong>a lotti</strong>, è idempotente e ripartibile: ogni avvio prende solo i primi N ancora scoperti,
                    quindi puoi ri-lanciarlo finché vuoi senza duplicati ed evitando il blocco IP.
                </p>

                <div className="flex items-center gap-3 mb-4">
                    <div className="border border-t22252a px-4 py-3 bg-t0e1014">
                        <div className="op-label text-[10px]">Ancora senza abilità (VG ≥ 74)</div>
                        <div className="op-mono font-bold text-2xl text-tff7a1a" data-testid="free-agents-remaining">
                            {(status?.free_agents_no_skills ?? 0).toLocaleString("it-IT")}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                    <label className="block">
                        <span className="op-label text-[11px]">Quanti per lotto</span>
                        <input
                            type="number"
                            min={1}
                            max={2000}
                            value={batchSize}
                            onChange={(e) => setBatchSize(e.target.value)}
                            className="mt-1 block w-32 bg-t0e1014 border border-t22252a px-3 py-2 op-mono text-tffffff focus:border-tff7a1a outline-none"
                            data-testid="free-agents-batch-input"
                        />
                    </label>
                    <label className="block">
                        <span className="op-label text-[11px]">VG minimo</span>
                        <input
                            type="number"
                            min={40}
                            max={99}
                            value={vgMin}
                            onChange={(e) => setVgMin(e.target.value)}
                            className="mt-1 block w-28 bg-t0e1014 border border-t22252a px-3 py-2 op-mono text-tffffff focus:border-tff7a1a outline-none"
                            data-testid="free-agents-vgmin-input"
                        />
                    </label>
                    <button
                        onClick={startFreeAgentsSkills}
                        disabled={busy || status?.running}
                        className="op-btn border border-tff7a1a text-tff7a1a hover:bg-tff7a1a hover:text-black px-5 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="sync-free-agents-skills-btn"
                    >
                        ↻ Scarica abilità svincolati ({Math.max(1, Math.min(2000, Number(batchSize) || 300))})
                    </button>
                </div>

                <div className="op-mono text-[11px] text-t8f95a3 mt-3" data-testid="free-agents-env-note">
                    {isProd
                        ? "✓ Sei in PRODUZIONE: lo scraping diretto è bloccato da pesdb.net. Usa il PONTE qui sotto: importa il file di abilità scaricato dalla Preview."
                        : "ⓘ Sei in PREVIEW: scrapa a lotti qui, poi usa il PONTE qui sotto per scaricare il file e caricarlo in Produzione."}
                </div>

                {/* Ponte Preview → Produzione */}
                <div className="border-t border-t22252a mt-5 pt-5">
                    <div className="op-label text-t2bb6ff mb-2">// PONTE PREVIEW → PRODUZIONE</div>
                    <p className="text-tc9ccd3 text-sm leading-relaxed mb-4">
                        pesdb.net blocca l&apos;IP di produzione, quindi le abilità si scrapano in <strong>Preview</strong> e si
                        trasferiscono in <strong>Produzione</strong> via file (come per i valori Transfermarkt):
                        in Preview <strong>① Scarica file abilità</strong>, in Produzione <strong>② Carica file abilità</strong>.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={exportSkills}
                            disabled={busy}
                            className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-5 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid="export-skills-btn"
                        >
                            ⬇ ① Scarica file abilità (VG ≥ {Math.max(40, Math.min(99, Number(vgMin) || 74))})
                        </button>
                        <label
                            className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-5 py-2 cursor-pointer transition-colors"
                            data-testid="import-skills-label"
                        >
                            ⬆ ② Carica file abilità
                            <input
                                type="file"
                                accept="application/json,.json"
                                onChange={importSkills}
                                disabled={busy}
                                className="hidden"
                                data-testid="import-skills-input"
                            />
                        </label>
                    </div>
                    {importResult && (
                        <div className="op-mono text-[12px] text-t7ce38b mt-3" data-testid="import-skills-result">
                            ✓ Importate: {importResult.applied} applicate · {importResult.received} ricevute
                            {importResult.unknown > 0 && ` · ${importResult.unknown} pes_id sconosciuti`}
                        </div>
                    )}
                </div>
            </div>

            <div className="op-card p-6 mt-6">
                <div className="op-label text-t8f95a3 mb-3">// COME FUNZIONA</div>
                <ul className="text-tc9ccd3 text-sm space-y-2 leading-relaxed list-disc list-inside">
                    <li>Il sync gira in background sul server. Puoi chiudere questa pagina, continua comunque.</li>
                    <li>Indice → popola subito la lista cercabile (nome, ruolo, overall, ecc.).</li>
                    <li>Dettagli → arricchisce con statistiche complete (~30 voci), foto, stile di gioco, resistenza infortuni.</li>
                    <li>I dati vengono <strong>aggiornati</strong> se già presenti: nessuna duplicazione.</li>
                    <li>Quando lanci un sync, mantiene la stessa idempotenza: i giocatori scomparsi da pesdb restano in DB (puoi vederlo dal campo &quot;last_index_sync&quot;).</li>
                </ul>
            </div>
        </div>
    );
}

function Stat({ label, value, accent = "rgb(var(--tc-ffffff))", small }) {
    return (
        <div className="p-5">
            <div className="op-label">{label}</div>
            <div
                className={`mt-2 op-mono font-bold ${small ? "text-lg" : "text-2xl"}`}
                style={{ color: accent }}
            >
                {value}
            </div>
        </div>
    );
}

function ProgressBar({ label, value, total, pct, unit }) {
    return (
        <div data-testid={`progress-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-2">
                <span className="op-label">{label}</span>
                <span className="op-mono text-sm text-tffffff">
                    {value.toLocaleString("it-IT")} / {total.toLocaleString("it-IT")} {unit} ({pct.toFixed(1)}%)
                </span>
            </div>
            <div className="h-2 bg-t1a1d24 border border-t22252a">
                <div
                    className="h-full bg-t2bb6ff transition-all duration-300"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function ActionCard({ title, desc, onClick, disabled, testId, accent }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className="text-left p-4 border border-t22252a hover:border-tffffff disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            data-testid={testId}
        >
            <div className="op-label" style={{ color: accent }}>{title}</div>
            <p className="text-tc9ccd3 text-sm mt-2 leading-relaxed">{desc}</p>
        </button>
    );
}

function NewsReport({ report }) {
    const [showNew, setShowNew] = useState(false);
    const [showRemoved, setShowRemoved] = useState(false);
    if (!report) return null;

    const ranAt = report.ran_at ? new Date(report.ran_at) : null;
    const newPlayers = report.new_players || [];
    const removedPlayers = report.removed_players || [];

    return (
        <div className="op-card p-6 mt-6" data-testid="index-news-report">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <div className="op-label text-t7ce38b">// NOVITÀ DI OGGI (ultimo Sync indice)</div>
                {ranAt && (
                    <div className="op-mono text-[11px] text-t8f95a3" data-testid="index-news-ranat">
                        {ranAt.toLocaleString("it-IT")}
                    </div>
                )}
            </div>
            {!report.complete && (
                <div className="op-mono text-[11px] text-tff7a1a mb-3" data-testid="index-news-incomplete">
                    ⚠ Scansione incompleta ({report.pages_done}/{report.total_pages} pagine): i candidati alla
                    rimozione non sono affidabili e non vengono mostrati. Ri-lancia il Sync indice.
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-3">
                {/* Nuovi giocatori */}
                <div className="border border-t22252a bg-t0e1014 p-4">
                    <div className="flex items-center justify-between">
                        <span className="op-label text-[10px]">Nuovi giocatori comparsi</span>
                        <span className="op-mono font-bold text-2xl text-t7ce38b" data-testid="index-news-new-count">
                            {(report.new_count ?? 0).toLocaleString("it-IT")}
                        </span>
                    </div>
                    {newPlayers.length > 0 && (
                        <>
                            <button
                                onClick={() => setShowNew((v) => !v)}
                                className="op-mono text-[11px] text-t2bb6ff hover:underline mt-2"
                                data-testid="index-news-new-toggle"
                            >
                                {showNew ? "Nascondi elenco" : `Mostra elenco (${newPlayers.length})`}
                            </button>
                            {showNew && (
                                <ul className="mt-2 max-h-72 overflow-auto divide-y divide-t1a1d24" data-testid="index-news-new-list">
                                    {newPlayers.map((p) => (
                                        <li key={p.pes_id} className="flex items-center justify-between py-1.5 text-sm">
                                            <span className="text-tffffff truncate mr-2">{p.name || `#${p.pes_id}`}</span>
                                            <span className="op-mono text-[11px] text-t8f95a3 whitespace-nowrap">
                                                {p.team_pes || "—"} · OVR {p.overall ?? "—"}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>

                {/* Candidati rimozione */}
                <div className="border border-t22252a bg-t0e1014 p-4">
                    <div className="flex items-center justify-between">
                        <span className="op-label text-[10px]">Candidati rimozione (spariti da PESDB)</span>
                        <span className="op-mono font-bold text-2xl text-tff3b30" data-testid="index-news-removed-count">
                            {report.complete ? (report.removed_count ?? 0).toLocaleString("it-IT") : "—"}
                        </span>
                    </div>
                    {report.complete && removedPlayers.length > 0 && (
                        <>
                            <button
                                onClick={() => setShowRemoved((v) => !v)}
                                className="op-mono text-[11px] text-t2bb6ff hover:underline mt-2"
                                data-testid="index-news-removed-toggle"
                            >
                                {showRemoved ? "Nascondi elenco" : `Mostra elenco (${removedPlayers.length})`}
                            </button>
                            {showRemoved && (
                                <ul className="mt-2 max-h-72 overflow-auto divide-y divide-t1a1d24" data-testid="index-news-removed-list">
                                    {removedPlayers.map((p) => (
                                        <li key={p.pes_id} className="flex items-center justify-between py-1.5 text-sm">
                                            <span className="text-tffffff truncate mr-2">{p.name || `#${p.pes_id}`}</span>
                                            <span className="op-mono text-[11px] text-t8f95a3 whitespace-nowrap">
                                                {p.team_pes || "—"} · OVR {p.overall ?? "—"}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            </div>
            <p className="text-t8f95a3 text-[11px] mt-3 leading-relaxed">
                I &quot;candidati rimozione&quot; restano nel DB e non vengono cancellati automaticamente: sono pes_id PESDB
                già visti in passato ma assenti dall&apos;indice corrente (esclusi svincolati sintetici e TM-only).
            </p>
        </div>
    );
}


const FIELD_LABELS = {
    name: "Nome", position: "Ruolo", overall: "Overall/VG", nationality: "Nazionalità",
    age: "Età", height: "Altezza", weight: "Peso", foot: "Piede", squad_number: "Numero",
    league: "Lega", playing_style: "Stile di gioco", playing_style_att: "Stile ATT",
    playing_style_def: "Stile DIF", region: "Regione", max_level: "Livello max",
    weak_foot_usage: "Uso piede debole", weak_foot_accuracy: "Precisione piede debole",
    injury_resistance: "Resistenza infortuni", team_pes: "Squadra PES", form: "Condizione",
    player_skills: "Abilità", ai_playing_styles: "Stili IA", stats: "Statistiche",
    position_ratings: "Mappa ruoli", image_url: "Immagine PESDB",
    market_value_current: "Valore di mercato", tm_value_date: "Data valore",
    tm_image: "Foto TM", tm_link: "Link TM", tm_birthdate: "Data nascita (età TM)",
};

function fieldLabel(k) {
    return FIELD_LABELS[k] || k;
}

function fmtVal(v) {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return "(dati)";
    const s = String(v);
    return s.length > 42 ? s.slice(0, 39) + "…" : s;
}

function ImportPreviewModal({ preview, busy, onConfirm, onCancel }) {
    const { diff } = preview;
    const players = diff.players || [];
    const allKeys = [];
    const fieldKeys = {}; // field -> [keys]
    players.forEach((p) => (p.changes || []).forEach((c) => {
        allKeys.push(c.key);
        (fieldKeys[c.field] = fieldKeys[c.field] || []).push(c.key);
    }));
    const [accepted, setAccepted] = useState(() => new Set(allKeys));
    const [filter, setFilter] = useState("");
    const nothingChanges = allKeys.length === 0;

    const toggle = (key) => setAccepted((prev) => {
        const n = new Set(prev);
        n.has(key) ? n.delete(key) : n.add(key);
        return n;
    });
    const setMany = (keys, on) => setAccepted((prev) => {
        const n = new Set(prev);
        keys.forEach((k) => (on ? n.add(k) : n.delete(k)));
        return n;
    });
    const toggleField = (field) => {
        const keys = fieldKeys[field] || [];
        const allOn = keys.every((k) => accepted.has(k));
        setMany(keys, !allOn);
    };

    const fcEntries = Object.entries(diff.field_changes || {}).sort((a, b) => b[1] - a[1]);
    const shown = filter.trim()
        ? players.filter((p) => (p.name || "").toLowerCase().includes(filter.trim().toLowerCase()))
        : players;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={onCancel}
            data-testid="import-preview-modal"
        >
            <div
                className="op-card w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 shrink-0">
                    <div className="op-label text-te8ff00">⚖️ ANTEPRIMA IMPORT — DATI COMPLETI</div>
                    <div className="op-mono text-[11px] text-t8f95a3 mt-1">
                        {diff.received} giocatori nel file · {diff.total_changes} cambiamenti totali · Niente viene scritto finché non applichi.
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                        <span className="op-mono text-[11px] px-2 py-1 border border-t7ce38b/50 text-t7ce38b" data-testid="preview-new">
                            {diff.players_new} nuovi (da creare)
                        </span>
                        <span className="op-mono text-[11px] px-2 py-1 border border-tffd24b/50 text-tffd24b" data-testid="preview-changed">
                            {diff.players_changed} modificati
                        </span>
                        <span className="op-mono text-[11px] px-2 py-1 border border-t22252a text-t8f95a3" data-testid="preview-unchanged">
                            {diff.players_unchanged} invariati
                        </span>
                        {diff.skipped > 0 && (
                            <span className="op-mono text-[11px] px-2 py-1 border border-tff3b30/50 text-tff3b30">
                                {diff.skipped} saltati
                            </span>
                        )}
                    </div>
                    {diff.vg_will_recompute && (
                        <div className="op-mono text-[10px] text-tff7a1a mt-2">
                            ⚠ Se applichi cambiamenti PESDB, i VG di rosa verranno ricalcolati dall&apos;overall Authentic.
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {nothingChanges ? (
                        <div className="op-card p-5 text-t7ce38b text-sm text-center" data-testid="preview-no-changes">
                            ✓ Nessuna modifica: i dati in produzione sono già allineati al file.
                        </div>
                    ) : (
                        <>
                            {/* selezione rapida */}
                            <div className="flex flex-wrap items-center gap-2">
                                <button onClick={() => setMany(allKeys, true)} className="op-mono text-[11px] px-2 py-1 border border-t7ce38b/60 text-t7ce38b hover:bg-t7ce38b/10" data-testid="select-all-btn">
                                    ✓ Seleziona tutto
                                </button>
                                <button onClick={() => setMany(allKeys, false)} className="op-mono text-[11px] px-2 py-1 border border-tff3b30/60 text-tff3b30 hover:bg-tff3b30/10" data-testid="select-none-btn">
                                    ✗ Deseleziona tutto
                                </button>
                                <input
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="filtra per nome…"
                                    className="op-mono text-[11px] bg-t0e1014 border border-t22252a px-2 py-1 text-tc9ccd3 ml-auto w-48"
                                    data-testid="preview-name-filter"
                                />
                            </div>

                            {/* toggle per campo */}
                            {fcEntries.length > 0 && (
                                <div>
                                    <div className="op-label text-[10px] mb-2">// ACCENDI/SPEGNI UN CAMPO PER TUTTI</div>
                                    <div className="flex flex-wrap gap-2">
                                        {fcEntries.map(([f, n]) => {
                                            const keys = fieldKeys[f] || [];
                                            const allOn = keys.length > 0 && keys.every((k) => accepted.has(k));
                                            const someOn = keys.some((k) => accepted.has(k));
                                            return (
                                                <button
                                                    key={f}
                                                    onClick={() => toggleField(f)}
                                                    className={`op-mono text-[11px] px-2 py-1 border ${allOn ? "border-t7ce38b text-t7ce38b" : someOn ? "border-tffd24b text-tffd24b" : "border-t22252a text-t8f95a3"}`}
                                                    data-testid={`field-toggle-${f}`}
                                                >
                                                    {allOn ? "✓" : someOn ? "◐" : "○"} {fieldLabel(f)} <span className="opacity-70">×{n}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* dettaglio per giocatore */}
                            <div>
                                <div className="op-label text-[10px] mb-2">// DETTAGLIO ({shown.length} giocatori)</div>
                                <div className="space-y-2">
                                    {shown.map((p) => (
                                        <div key={p.pes_id} className="op-card p-3 bg-t0c0e11" data-testid={`preview-row-${p.pes_id}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-tffffff text-sm font-semibold">{p.name || `#${p.pes_id}`}</span>
                                                {p.is_new && <span className="op-mono text-[9px] px-1.5 py-0.5 border border-t7ce38b text-t7ce38b">NUOVO</span>}
                                                <span className="op-mono text-[9px] text-t5c6370">pes_id {p.pes_id}</span>
                                            </div>
                                            <div className="space-y-0.5 pl-1">
                                                {(p.changes || []).map((c) => {
                                                    const on = accepted.has(c.key);
                                                    const isNew = c.field === "__new__";
                                                    return (
                                                        <label
                                                            key={c.key}
                                                            className="op-mono text-[11px] flex flex-wrap items-center gap-2 cursor-pointer py-0.5"
                                                            data-testid={`change-${c.key}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={on}
                                                                onChange={() => toggle(c.key)}
                                                                className="accent-t7ce38b w-3.5 h-3.5"
                                                                data-testid={`change-cb-${c.key}`}
                                                            />
                                                            <span className={`px-1.5 py-0.5 text-[9px] border ${c.cat === "tm" ? "border-t2bb6ff/50 text-t2bb6ff" : "border-tff7a1a/50 text-tff7a1a"}`}>
                                                                {c.cat === "tm" ? "TM" : "PESDB"}
                                                            </span>
                                                            {isNew ? (
                                                                <span className="text-t7ce38b">Crea scheda giocatore (tutti i dati)</span>
                                                            ) : (
                                                                <>
                                                                    <span className="text-t8f95a3">{fieldLabel(c.field)}:</span>
                                                                    {c.complex ? (
                                                                        <span className="text-tffd24b">modificato</span>
                                                                    ) : (
                                                                        <>
                                                                            <span className="text-tff7a1a">{fmtVal(c.from)}</span>
                                                                            <span className="text-t5c6370">→</span>
                                                                            <span className="text-t7ce38b">{fmtVal(c.to)}</span>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-t22252a bg-t0e1014 shrink-0 flex items-center justify-between gap-3 flex-wrap">
                    <div className="op-mono text-[11px] text-t8f95a3" data-testid="preview-accepted-count">
                        {accepted.size} cambiamenti selezionati su {allKeys.length}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            className="op-btn op-btn-secondary px-4 py-2"
                            onClick={onCancel}
                            disabled={busy}
                            data-testid="preview-import-cancel"
                        >
                            Annulla
                        </button>
                        <button
                            className="op-btn op-btn-primary px-4 py-2 disabled:opacity-50"
                            onClick={() => onConfirm(Array.from(accepted))}
                            disabled={busy || accepted.size === 0}
                            data-testid="preview-import-confirm"
                        >
                            {busy ? "Applico…" : `Applica ${accepted.size} selezionati`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

